import { useId } from "react";
import { Props_SnippetAccordion } from "./Accordion.props";
import SnippetLink from '../Shared/Link/Link';
import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'

const ANIMATION_DURATION = 0.3;

const classAssignment = (prop: string | undefined | boolean, positiveClassName: string, negativeClassName = '') => {
    if (prop) {
        if (typeof prop === 'string') {
            return `${positiveClassName}--${prop}`
        }
        return positiveClassName
    }

    return negativeClassName
}

type AccordionRowProps = {
    accordionRow: Props_SnippetAccordion['repeatable_accordionRow'][number];
    index: number;
    isOpen: boolean;
    isDetailsOpen: boolean;
    onToggle: (index: number, event: React.MouseEvent) => void;
    onExitComplete: (index: number) => void;
};

const AccordionRow = ({ accordionRow, index, isOpen, isDetailsOpen, onToggle, onExitComplete }: AccordionRowProps) => {
    const { linkText, linkHref, linkStyle, linkTarget, linkAriaLabel, heading, text } = accordionRow;

    return (
        <details
            key={"accordion-row-" + index}
            className={`snippet-accordion__accordion ${isOpen ? "snippet-accordion__accordion--open" : ""}`}
            open={isDetailsOpen}
        >
            <summary
                className={`snippet-accordion__accordion-heading ${isDetailsOpen ? "snippet-accordion__accordion-heading--expanded" : ""}`}
                onClick={(e) => onToggle(index, e)}
            >
                {heading?.value ? heading.value : "Accordion " + (index + 1)}
            </summary>
            <AnimatePresence initial={false} onExitComplete={() => onExitComplete(index)}>
                {isOpen && (
                    <motion.div
                        className="snippet-accordion__accordion-container"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: ANIMATION_DURATION, ease: "easeInOut" }}
                    >
                        <div className="snippet-accordion__accordion-content">
                            {text?.value && (
                                <div className="snippet-accordion__accordion-text">{CompileMarkdown(text.value)}</div>
                            )}
                            {linkHref?.value && linkText?.value && (
                                <SnippetLink
                                    linkStyles={linkStyle?.value}
                                    className="snippet-accordion__accordion-link"
                                    url={linkHref?.value}
                                    openInNewTab={!!linkTarget?.value}
                                    rel={linkTarget ? "noopener noreferrer" : undefined}
                                    aria-label={linkAriaLabel?.value || linkText?.value}
                                >
                                    {linkText?.value}
                                </SnippetLink>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </details>
    );
};

const Accordion = (props: Props_SnippetAccordion) => {
    const { snippet, heading, repeatable_accordionRow } = props;
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [detailsOpenSet, setDetailsOpenSet] = useState<Set<number>>(new Set());
    const id = useId();
    const classHeadingVisibility: string = heading.visibility?.value ? 'snippet-accordion__heading--visible' : 'snippet-accordion__heading--hidden';
    const backgroundColorClass = classAssignment(snippet?.backgroundColor?.value, 'snippet-accordion--bg');
    const fontColorClass = classAssignment(snippet?.fontColor?.value, 'snippet-accordion--font');
    const modifierClasses = `${backgroundColorClass} ${fontColorClass}`;

    const handleToggle = (index: number, event: React.MouseEvent) => {
        event.preventDefault();
        
        if (openIndex === index) {
            setOpenIndex(null);
        } else {
            setOpenIndex(index);
            setDetailsOpenSet(prev => new Set(prev).add(index));
        }
    };

    const handleExitComplete = (index: number) => {
        setDetailsOpenSet(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
        });
    };

    return (
        <section className={`snippet-accordion ${modifierClasses}`}>
            <div className="snippet-accordion__container">
                <div className="snippet-accordion__container-heading">
                    <h2 className={`snippet-accordion__heading ${classHeadingVisibility}`}>
                        {heading.text.value}
                    </h2>
                    {heading.content?.value && (
                        <div className="snippet-accordion__description">
                            {CompileMarkdown(heading.content.value)}
                        </div>
                    )}
                </div>

                <div className="snippet-accordion__container-accordions">
                    {repeatable_accordionRow.map((accordionRow, index) => (
                        <AccordionRow
                            key={`accordion-row-${id}-${index}`}
                            accordionRow={accordionRow}
                            index={index}
                            isOpen={openIndex === index}
                            isDetailsOpen={detailsOpenSet.has(index)}
                            onToggle={handleToggle}
                            onExitComplete={handleExitComplete}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Accordion;
